import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
          <div className="max-w-md text-center">
            <div className="mb-4 text-6xl">😥</div>
            <h2 className="mb-2 text-xl font-semibold text-[var(--color-text)]">
              문제가 발생했습니다
            </h2>
            <p className="mb-6 text-[var(--color-text-muted)]">
              예상치 못한 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 rounded-lg bg-red-50 p-4 text-left dark:bg-red-950">
                <summary className="cursor-pointer font-medium text-red-700 dark:text-red-400">
                  오류 상세 정보 (개발 모드)
                </summary>
                <pre className="mt-2 overflow-auto text-xs text-red-600 dark:text-red-300">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={this.handleReset}
                className="rounded-lg border border-[var(--color-primary)] px-4 py-2 text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary)] hover:text-white"
              >
                다시 시도
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-white transition-colors hover:bg-[var(--color-primary-hover)]"
              >
                페이지 새로고침
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
